/**
 * Ads
 * Contains the logic necessary for initiating and displaying DFP Ads on the site
 * version 2.6.0
 * 
 * Using Semantic Versioning, please update accordingly to keep files in sync across code bases
 * 
 * Given a version number MAJOR.MINOR.PATCH, increment the:
 *  1. MAJOR version when you implement a full re-build of the architecture,
 *  2. MINOR version when you add functionality, and
 *  3. PATCH version when you implement bug fixes or make small  tweaks to current functionality.
 */
define('ads',[
    /* 3rd Party Libs */
    "jquery",

    /* General Libs */
    "logging",
    "utilities",
    "storage",
    "ModuleLoader",
    "performance",

    /* GDPR Stuff */
    "location",
    "onetrust",

    /* Ad Creative Libs */
    "creatives",

    /* Admiral Ad-Free */
    "admiral",

    /* DFP Libs */
    "AdPlatforms/dfp",
    "AdModules/lazyloading",
    "AdModules/delayedloading",
    "AdModules/autorefresh",
    "AdModules/buckets",
    "AdModules/headerbidding",
    "AdModules/adslots",
    "AdModules/targeting",
    "AdModules/manualloading",
    "AdModules/ratelimit",
    "AdModules/disableflags",

    /* DFP Models */
    "AdModels/PageConfig",
    "AdModels/Config"
], function (
    /* 3rd Party Libs */
    $,
    /* General Libs */
    logging, utilities, storage, moduleloader, performance,
    /* GDPR Stuff */
    location, onetrust,
    /* Ad Creative Libs */
    creatives,
    /* Admiral Ad-Free */
    admiral,
    /* DFP Libs */
    dfp, lazyloading, delayedloading, autorefresh, buckets, headerbidding, adslots, targeting, manualloading, limit, disableflags,
    /* DFP Models */
    PageConfig, Config
) {
    "use strict";

    var _ModuleName = "Ads";

    var _config = {};
    var _data = {
        slots: [],
        pageconfig: {},
        initialized: false,
        monitorName: 'dfp',
        monitorSubscriptionName: 'dfpjs'
    }
    var _perfMonitor = 'DFP';

    /*
     * Initialize global dfp object, used for ads on the pages to register themselves
     */
    var init = function () {
        performance.registerMonitor(_perfMonitor);

        // create a deferred response
        var dfd = $.Deferred();

        // Check if ads have been disabled
        if (_adsAreDisabled()) {
            ;
            return dfd.resolve();
        }

        // Initialize configuration object
        $.extend(_config, new Config());

        console.log("Ad Config Loaded: %o", _config);

        if (Object.keys(_config).length === 0) {
            logging.error("Failed to load Ad Configuration", _ModuleName);
            return dfd.resolve();
        }

        // Initialize Admiral Ad-Free
        admiral.init();

        performance.tick(_perfMonitor, 'Admiral Ad-Free Subscription Check Started');
        admiral.checkUserSubscribed(function () {
            // USER IS SUBSCRIBED, NO ADS WILL LOAD
            ;

            // mark ads as disabled
            _config.enabled = false;

        }, function () {
            // USER IS NOT SUBSCRIBED, LOAD THE ADS
            ;

            // mark whether ads are enabled or not
            _config.enabled = !dfp.adsAreDisabled() && !_data.pageconfig.disabled;

            // run initializations
            initWork();

        }, function () {
            // DO THIS WHETHER SUBSCRIBED OR NOT

            // update performance monitor
            performance.tick(_perfMonitor, 'Finished Admiral Ad-Free Subscription Check');

            // resolve promise
            dfd.resolve();
        }, true)

        // return promise
        return dfd.promise();
    }

    var _adLoadStart;
    var _adLoadEnd;
    var initWork = function () {
        // if already initiallized, we don't need to do it again
        if (_data.initialized) { return; }

        // Initialize Creative JavaScript Functions
        creatives.init(_config);

        // Initialize Ad Slot Module
        adslots.init(_config);

        // Initialize Manual Ad Loading
        manualloading.init(_config);

        // Initialize Lazy Loading
        lazyloading.init(_config);

        // Initialize Delayed Loading
        delayedloading.init(_config);

        // Initialize Auto Refresh
        autorefresh.init(_config);

        // Initialize DFP Targeting
        targeting.init(_config);

        // Initialize Disable Flag Support
        disableflags.init(_config);

        // Initialize Rate Limiting Library
        limit.init(_config);

        // Create Ready Monitor for DFP Refresh
        utilities.registerReadyMonitor(_data.monitorName, function () {
            ;
            _adLoadStart = new Date();
            dfp.refreshAllSlots();

            performance.tick(_perfMonitor, 'Request Refresh from DFP');

            ;
            targeting.printTargetDataToConsole();

            // Starting Lazy Loading
            ;
            lazyloading.start(_data.pageconfig);

            // Starting Delayed Loading
            ;
            delayedloading.start(_data.pageconfig);

            // handle any after ad request callbacks
            _handleAdsRequestedCallbacks();
        });

        // Subscribe to Ready Monitor
        utilities.subscribeToReadyMonitor(_data.monitorName, _data.monitorSubscriptionName);

        window.googletag = window.googletag || {};
        window.googletag.cmd = window.googletag.cmd || [];

        // Set up default page config
        _data.pageconfig = new PageConfig();
        _data.pageconfig.source = utilities.getParameter(document.URL, "utm_source");
        _data.pageconfig.medium = utilities.getParameter(document.URL, "utm_medium");
        _data.pageconfig.campaign = utilities.getParameter(document.URL, "utm_campaign");
        _data.pageconfig.pagenum = utilities.getParameter(document.URL, 'p');
        _data.pageconfig.enableCookies = _config.cookies.enabled;
        _data.pageconfig.fluid = _config.fluid; // Pass through entire fluid configuration

        // In the EU, disable cookies unless explicitly allowed 
        var useCookies = storage.getSessionStorage(_config.storage.useCookies);
        if (typeof (useCookies) === 'boolean') {
            // enable or disable cookies based on what was stored
            _data.pageconfig.enableCookies = useCookies ? _config.cookies.enabled : _config.cookies.disabled;
        } else {
            location.isCookieLawZone(function () {
                // Disable Cookies until authorized
                _data.pageconfig.enableCookies = _config.cookies.disabled;
                storage.setSessionStorage(_config.storage.useCookies, false);

                ;
                ;
                onetrust.registerScript(onetrust.categories.Targeting, function () {
                    // Authorization received, enable cookies
                    ;
                    storage.setSessionStorage(_config.storage.useCookies, true);
                    _data.pageconfig.enableCookies = _config.cookies.enabled;
                });

                // If OneTrust fails to initialize, enable cookies
                setTimeout(function () {
                    if (!onetrust.isInitialized()) {
                        // OneTrust isn't working fast enough. enabling cookies
                        ;
                        storage.setSessionStorage(_config.storage.useCookies, true);
                        _data.pageconfig.enableCookies = _config.cookies.enabled;
                    }
                }, 1e3);

            }, function () { storage.setSessionStorage(_config.storage.useCookies, true); });

            // default cookies to enabled while we decide if they should be enabled/disabled
            useCookies = true;
            _data.pageconfig.enableCookies = _config.cookies.enabled;
        }

        // Only do this stuff if cookies are allowed
        if (useCookies) {
            ;
            // Initialize Header Bidding
            _initializeHeaderBidding();

            // Initialize buckets
            buckets.init(_config);

            // Get current page veiw count, default to 0
            var pageCount = storage.getSessionStorage(_config.storage.pageViewCount) || 0;
            // Increment and store page view count
            storage.setSessionStorage(_config.storage.pageViewCount, ++pageCount);
            _data.pageconfig.pagecount = ((typeof pageCount === 'undefined') ? "" : String(pageCount));
            ;

            //Grab source from query parameter or from session
            var source = _data.pageconfig.source || storage.getSessionStorage(_config.storage.dfpSource);
            storage.setSessionStorage(_config.storage.dfpSource, source);
            _data.pageconfig.source = source;

            //Grab medium from query parameter or from session
            var medium = _data.pageconfig.medium || storage.getSessionStorage(_config.storage.dfpMedium);
            storage.setSessionStorage(_config.storage.dfpMedium, medium);
            _data.pageconfig.medium = medium;

            //Grab campaign from query parameter or from session
            var campaign = _data.pageconfig.campaign || storage.getSessionStorage(_config.storage.dfpCampaign);
            storage.setSessionStorage(_config.storage.dfpCampaign, campaign);
            _data.pageconfig.campaign = campaign;
        } else {
            // Disable Storage for Rate Limit Lib
            limit.disableStorage();

            // Disable personalized ads if in cookie law zone.
            location.isCookieLawZone(function () {
                _disablePersonalizedAds(true);
            }, function () { });
        }

        performance.tick(_perfMonitor, 'Requesting DFP Load Scripts');

        // Load DFP Scripts
        dfp.loadScripts();

        ;

        ;
        window.bnm = window.bnm || {};
        window.bnm.ads = window.bnm.ads || {};
        window.bnm.ads.loadNewAds = _loadNewAds;

        _data.initialized = true;
        ;

        performance.tick(_perfMonitor, 'Finished Initialization');
    }

    var _preloaded = false;
    var preload = function () {
        ;
        // if preload data was found, run through the process
        if (window.bnm && window.bnm.ads && window.bnm.ads.preload) {
            var config = JSON.parse(window.bnm.ads.preload);
            if (config) {
                this.registerPageConfig(config.pageConfig);
                for (var i = 0; i < config.adSlots.length; i++) {
                    var slot = config.adSlots[i];
                    this.registerAdSlot(slot);
                }

                _preloaded = true;

                logging.log("Finished preloading ad configuration.", _ModuleName);
                return true;
            } else {
                logging.error("Failed to parse Ad Config Preload Data. Will fall back to standard loading with a performance impact.", _ModuleName);
            }
        } else {
            logging.warn("Ad Config Preload Data Not Found. Will fall back to standard loading with a performance impact.", _ModuleName);
        }

        return false;
    }

    /**
     * Enable/Disable personalized ads
     * @param {boolean} restrict whether or not to restrict ad personalization
     */
    var _disablePersonalizedAds = function (restrict) {
        if (typeof restrict === undefined) {
            restrict = false;
        }

        ;
        window.googletag.pubads().setPrivacySettings({
            'restrictDataProcessing': restrict
        });
    }

    /**
     * Register ad slot configuration with global dfp object
     * @param {any} config ad slot configuration object
     * @returns {boolean} true if slot registered successfully, false otherwise
     */
    var registerAdSlot = function (config) {
        // if ads are disabled, no need to register
        if (!_config.enabled) { return false; }

        // if slots were preloaded, find slot and update as needed
        if (_preloaded) {
            var preloadedSlot = _data.slots.find(function (slot) { return slot.id === config.id; });
            if (preloadedSlot !== undefined) {
                preloadedSlot.dom = config.dom;
                ;
                return true;
            }

            logging.warn("Ad configurations were preloaded, but this slot was not found. Was it missed during preload?", _ModuleName);
            return false;
        }

        // make sure slot matches device type
        if (utilities.IsPhone() !== config.mobile) {
            ;
            return false;
        }

        var slot = adslots.initAdSlot(config);

        if (limit.enabled() && !limit.canServe(slot)) {
            ;
            return false;
        }

        if (disableflags.enabled() && !disableflags.canServe(slot)) {
            ;
            return false;
        }

        ;
        _data.slots.push(slot);
        return true;
    }

    /*
     * Register page configuration with global dfp object
     */
    var registerPageConfig = function (config) {
        if (_preloaded) {
            logging.warn("DFP Page Config already exists. Will not register again. It was preloaded.", _ModuleName);
            return;
        }

        $.extend(_data.pageconfig, config);
    }

    /*
     * Start DFP processes (run after ads are all registered)
     */
    var start = function () {
        performance.tick(_perfMonitor, 'Starting Ad Load Process');

        // Ensure ads are enabled before we proceed
        if (_adsAreDisabled()) {
            ;
            _hideAdSlots();
            _handleAdsLoadedCallbacks();
            _handleAdsRequestedCallbacks();
            return;
        }

        if (_data.initialized === false) {
            logging.error("Ads system was not initialized properly and will not be started.", _ModuleName);
            _handleAdsLoadedCallbacks();
            _handleAdsRequestedCallbacks();
            return;
        }

        // Let's drop some DFP config details on the console even in prod for the ad-ops team
        console.log("DFP Configuration: %o", _data.pageconfig);
        console.log("DFP Ad Slots: %o", _data.slots.map(function (s) { return { id: s.id, position: s.position }; }));

        // If we have no ad slots, no need to continue
        if (_data.slots.length === 0) {
            logging.log("No Ad Slots on the Page", _ModuleName);
            _handleAdsLoadedCallbacks();
            _handleAdsRequestedCallbacks();
            return;
        }

        // Start Ad Slot Module
        adslots.start(_data.pageconfig);

        // Start Targeting Module
        targeting.start(_data.pageconfig);

        disableflags.start();

        // Start Manual Ad Load Module
        manualloading.start(_data.pageconfig);

        // Start Rate Limit Module
        limit.start();

        performance.tick(_perfMonitor, 'Queueing Processes with DFP');

        ;

        ;
        dfp.enqueue(_loadDFPConfiguration);

        ;
        dfp.enqueue(_loadInitialSlots);

        ;
        dfp.enqueue(_registerAutoRefreshSlots);

        ;
        dfp.enqueue(function () {
            performance.tick(_perfMonitor, 'Enabling DFP Services');
            dfp.enableServices();
        });

        ;
        dfp.enqueue(_confirmReady);
    }

    var _hideAdSlots = function() {
        if (_config.customAdSlotClass) {
            $('.' + _config.customAdSlotClass).hide();
        }

        $('.DFPAdSlot').hide();        
    }

    /**
     * Initialize header bidding libraries
     */
    var _initializeHeaderBidding = function () {
        performance.tick(_perfMonitor, 'Initialize Header Bidding');

        headerbidding.init(_config, _data.monitorName);
    }

    /**
     * Instruct header bidding libraries to start the bidding
     */
    var _startHeaderBidding = function (slots) {
        performance.tick(_perfMonitor, 'Starting Header Bidding');

        headerbidding.start(slots, _data.pageconfig);
    }

    /**
     * Load DFP Configuration, including targeting parameters and slot positions
     */
    var _loadDFPConfiguration = function () {
        ;

        performance.tick(_perfMonitor, 'Loading DFP Configuration');

        // Load DFP Targeting
        targeting.loadTargeting("dfp");

        // Load Remaining DFP Configuration
        dfp.loadConfiguration(_data.pageconfig, _handleAdLoaded, _config.targetmapping);
    }

    var _adsLoadedCount = 0;
    var _handleAdLoaded = function (event) {
        // log to the console how long it took to load the first ad
        if (_adLoadEnd === undefined) {
            _adLoadEnd = new Date();
            var elapsed = _adLoadEnd.getTime() - _adLoadStart.getTime();
            logging.log("DFP Response received after " + elapsed + "ms", _ModuleName);
        }

        // Grab line item id
        _storeLineItemId(event);

        // Run dom updates on load
        dfp.adDomUpdatesOnLoad(event);

        if (--_adsLoadedCount === 0) {
            _handleAllAdsLoaded();
        }

        // Handle Slot Reference Cleanup
        var id = event.slot.getSlotElementId();
        _handleCleanup(id);
    }

    var _storeLineItemId = function (event) {
        var slotId = event.slot.getSlotElementId();
        var slot = _data.slots.find(function (s) { return s.id === slotId; });
        if (slot) {
            slot.lineItemId = event.lineItemId;
        }
    }

    var _handleCleanup = function (requestId) {
        if (_config.cleanup) {
            ;
            var slot = _data.slots.find(function (s) { return s.id === requestId; });
            if (slot) {
                if (!(autorefresh.enabled() && slot.autorefresh)) {
                    // remove from own slots
                    _removeSlot(slot);

                    // remove from lazy loading
                    if (lazyloading && lazyloading.enabled() && slot.lazyloading) {
                        lazyloading.removeSlot(slot);
                    }

                    // remove from header bidding
                    if (headerbidding && headerbidding.enabled()) {
                        headerbidding.removeSlot(slot);
                    }

                    // remove from delayed loading
                    if (delayedloading && delayedloading.enabled()) {
                        delayedloading.removeSlot(slot);
                    }

                    // remove from manual loading
                    if (manualloading && manualloading.enabled() && slot.manual) {
                        manualloading.removeSlot(slot);
                    }
                } else {
                    logging.warn("Slot " + slot.id + " marked for auto refresh and will not be removed", _ModuleName);
                }
            } else {
                logging.error("Unable to find slot by id " + requestId + ". Cleanup process failed.", _ModuleName);
            }
        }
    }

    /**
     * Removes slot from being tracked
     * @param {any} slot Slot definition
     */
    var _removeSlot = function (slot) {
        try {
            var index = _data.slots.findIndex(function (s) { return s.id === slot.id; });
            if (index >= 0) {
                ;
                _data.slots.splice(index, 1);
                ;
            } else {
                logging.error("Unable to locate slot with id " + slot.id + " so it will not be removed", _ModuleName);
            }
        } catch (error) {
            logging.error("Failed to remove slot from auto refresh", _ModuleName);
            logging.error(error, _ModuleName);
        }
    }

    /**
     * Handles any operations needed once all ads have loaded
     */
    var _handleAllAdsLoaded = function () {
        ;

        performance.complete(_perfMonitor);

        // Check for possible road block ads
        _detectRoadBlockAds();

        // Start Auto Refresh
        ;
        autorefresh.start();

        // handle callbacks that were requested to run after ads are loaded
        _handleAdsLoadedCallbacks();
    }

    var _detectRoadBlockAds = function () {
        ;
        for (var i = 0; i < _data.slots.length; i++) {
            // if more than one ad slot has the same line item id, it's likely a roadblock ad
            if (_data.slots[i].lineItemId && _data.slots.filter(function (s) { return s.lineItemId === _data.slots[i].lineItemId; }).length > 1) {
                ;
                _data.slots[i].isRoadBlock = true;
            }
        }
    }

    /**
     * Load the initial slots and mark others for lazyloading, delayed loading, or manual if eligible
     */
    var _loadInitialSlots = function () {
        ;

        performance.tick(_perfMonitor, 'Loading Initial Slots');

        var slots = [];
        for (var i = 0; i < _data.slots.length; i++) {
            var slot = _data.slots[i];

            if (delayedloading.enabled() && delayedloading.isEligibleForDelayedLoad(slot)) {
                // if delayed loading is enabled and slot is eligible
                delayedloading.registerSlot(slot);
            } else if (lazyloading.enabled() && lazyloading.isEligibleForLazyLoad(slot) && !lazyloading.isInView(slot)) {
                // if lazy loading is enabled, slot is eligible and slot is not in view
                lazyloading.registerSlot(slot);
            } else if (manualloading.enabled() && slot.manual) {
                // if marked for manual loading, register with module instead of loading
                manualloading.registerSlot(slot);
            } else {
                // if no special cases, just load it normally
                slots.push(slot);
            }
        }

        if (slots) {
            // start the header bidding on the slots
            _startHeaderBidding(slots);

            _adsLoadedCount = slots.length;
            ;
            if (!dfp.loadSlots(slots, _data.pageconfig)) {
                logging.warn("Not all slots were loaded successfully", _ModuleName);
            } else {
                ;
            }
        } else {
            logging.warn("No slots eligible for initial load", _ModuleName);
        }
    }

    /**
     * Register ad slots for auto refresh if eligible
     */
    var _registerAutoRefreshSlots = function () {
        ;
        autorefresh.registerSlots(_data.slots);
    }

    var _confirmReady = function () {
        performance.tick(_perfMonitor, 'Confirm Main ads Ready');

        // Confirm Ready with Ready Monitor
        ;
        utilities.confirmReady(_data.monitorName, _data.monitorSubscriptionName);
    }

    // attach function to window that we can use to load new ads
    var _loadNewAds = function () {
        ;
        moduleloader.loadPendingModules($('.DFPAdSlot')).done(function (modules) {
            if (modules) {
                // get slot ids from modules
                var slotIds = modules.filter(function (m) { return m !== null; }).map(function (m) { return m.slotId; });

                // pull slot data based on ids
                var slots = _data.slots.filter(function (s) { return slotIds.includes(s.id) && s.loaded === false; });

                if (slots.length > 0) {
                    ;
                    console.log("New Ad Slots", slots);

                    ;
                    // register slots as manually loaded
                    manualloading.registerSlots(slots);

                    ;
                    // register slots with header bidding
                    headerbidding.registerSlots(slots);

                    ;
                    // load manually
                    manualloading.loadAdsManually(slots.map(function (s) { return s.id; }));

                    ;
                } else {
                    ;
                }
            } else {
                ;
            }
        });
    }

    // Handle running some callbacks after ads have loaded
    var _adsLoaded = false;
    var _afterAdLoadCallbacks = [];
    var runAfterAdsLoaded = function (callback) {
        ;
        if (_adsLoaded) {
            if (typeof callback === 'function') {
                ;
                callback();
            }
        } else {
            ;
            _afterAdLoadCallbacks.push(callback);
        }
    }

    var _handleAdsLoadedCallbacks = function () {
        ;
        _adsLoaded = true;
        for (var i = 0; i < _afterAdLoadCallbacks.length; i++) {
            var callback = _afterAdLoadCallbacks[i];
            if (typeof callback === 'function') {
                callback();
            }
        }
    }

    var _adsRequested = false;
    var _afterAdRequestCallbacks = [];
    var runAfterAdsRequested = function (callback) {
        ;
        if (_adsRequested) {
            if (typeof callback === 'function') {
                ;
                callback();
            }
        } else {
            ;
            _afterAdRequestCallbacks.push(callback);
        }
    }

    var _handleAdsRequestedCallbacks = function () {
        ;
        _adsRequested = true;
        for (var i = 0; i < _afterAdRequestCallbacks.length; i++) {
            var callback = _afterAdRequestCallbacks[i];
            if (typeof callback === 'function') {
                callback();
            }
        }
    };

    var _adsAreDisabled = function () {
        // config has an enabled property set to false
        return (_config && typeof _config.enabled === 'boolean' && !_config.enabled)
            // dfp specifically says ads are disabled
            || dfp.adsAreDisabled()
            // dfp page configuration is loaded and marked as disabled
            || (_data && _data.pageconfig && typeof _data.pageconfig.disabled === 'boolean' && _data.pageconfig.disabled);
    };

    return {
        init: init,
        preload: preload,
        start: start,
        registerAdSlot: registerAdSlot,
        registerPageConfig: registerPageConfig,
        runAfterAdsLoaded: runAfterAdsLoaded,
        runAfterAdsRequested: runAfterAdsRequested,
        isPreloaded: (function () { return _preloaded; })
    }
});
